import { useEffect, useState } from 'react'
import type { FamilySession } from '@core/session'
import { TabBar, type TabItem } from '@design/components'
import { HomeIcon, InsightsIcon, LettersIcon, PaymentsIcon, PlusIcon, SearchIcon } from './icons'
import { TodosTab } from '@features/todos/TodosTab'
import { AddTab } from '@features/add/AddTab'
import { SearchScreen } from '@features/search/SearchScreen'
import { LettersTab } from '@features/letters/LettersTab'
import { ProfileSheet } from './ProfileSheet'
import { HomeScreen } from '@features/home/HomeScreen'
import { PaymentsTab } from '@features/payments/PaymentsTab'
import { refreshBadge } from '@core/badge'

/**
 * The administrator interface: five tabs, per docs/spec.md §30.
 *
 * Calendar and Planner are deliberately NOT tabs — they are views inside
 * Payments, because they are the same data and splitting them would split the
 * mental model of "my obligations". Settings, family and suppliers live behind
 * the avatar, which is why there is no More tab.
 *
 * Every tab below Home is a placeholder until its phase. Phase 2 only has to
 * prove that the right shell renders for the right role.
 */
const TABS: TabItem[] = [
  { id: 'home', label: 'Vandaag', icon: <HomeIcon /> },
  { id: 'payments', label: 'Betalingen', icon: <PaymentsIcon /> },
  { id: 'add', label: '', icon: <PlusIcon /> },
  { id: 'letters', label: 'Brieven', icon: <LettersIcon /> },
  // Inzicht's slot, for now. spec §939 is explicit that Insights has zero
  // value until there are months of data — it would show an empty chart and
  // one supplier, and teach everyone the tab is not worth opening. A list that
  // is used every day is a better tenant of the fifth slot until then, and
  // Insights takes it back in V1.1 when it has something to say.
  { id: 'todos', label: 'Te doen', icon: <InsightsIcon /> },
]

export function AdminShell({ session, onSignOut }: { session: FamilySession; onSignOut: () => void }) {
  const [tab, setTab] = useState('home')
  const [profileOpen, setProfileOpen] = useState(false)
  const [searching, setSearching] = useState(false)

  // The icon badge — the closest thing iOS gives a web app to a home-screen
  // widget. Repainted whenever you come back to the app, because the count is
  // only worth anything if it is not stale.
  useEffect(() => {
    void refreshBadge(session.userId, true)
    const onVisible = () =>
      document.visibilityState === 'visible' && void refreshBadge(session.userId, true)
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [session.userId, tab])

  return (
    <>
      <header className="shrink-0 pad-safe-top px-4 pb-3 flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold leading-tight">
            {tab === 'home' ? greeting(session.displayName) : labelFor(tab)}
          </h1>
          {tab === 'home' && (
            <p className="text-sm text-muted">{today()}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => setSearching(true)}
          aria-label="Zoeken"
          className="w-9 h-9 rounded-full grid place-items-center text-muted"
        >
          <SearchIcon />
        </button>
        <button
          onClick={() => setProfileOpen(true)}
          aria-label="Instellingen"
          className="w-9 h-9 shrink-0 rounded-full bg-ink text-canvas grid place-items-center text-sm font-medium"
        >
          {initials(session.displayName)}
        </button>
        </div>
      </header>

      {searching && <SearchScreen onClose={() => setSearching(false)} />}

      {tab === 'home' && <HomeScreen session={session} />}
      {tab === 'payments' && <PaymentsTab userId={session.userId} spaces={session.spaces} session={session} />}
      {tab === 'letters' && <LettersTab session={session} />}
      {tab === 'add' && (
        <AddTab
          session={session}
          onDone={() => setTab('payments')}
          onClose={() => setTab('home')}
        />
      )}
      {tab === 'todos' && <TodosTab session={session} />}

      <TabBar tabs={TABS} active={tab} onSelect={setTab} />

      {profileOpen && (
        <ProfileSheet
          session={session}
          onClose={() => setProfileOpen(false)}
          onSignOut={onSignOut}
        />
      )}
    </>
  )
}

function labelFor(tab: string) {
  return (
    { home: 'Vandaag', payments: 'Betalingen', add: 'Toevoegen', letters: 'Brieven', todos: 'Te doen' }[
      tab
    ] ?? ''
  )
}

function greeting(name: string) {
  const h = new Date().getHours()
  const part = h < 6 ? 'Goedenacht' : h < 12 ? 'Goeiemorgen' : h < 18 ? 'Goeiemiddag' : 'Goeieavond'
  return name ? `${part}, ${name.split(' ')[0]}` : part
}

function today() {
  return new Intl.DateTimeFormat('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(),
  )
}

function initials(name: string) {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?'
  )
}
